import React, { useEffect } from "react";
import { Form, Input, Button, Card, Spin } from "antd";
import { Save, Mail } from "lucide-react";
import { useGetCmsConfig, useSaveCmsConfig } from "../../../hooks/api/cms/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

interface IEnquirySettingsData {
  to?: string;
  cc?: string[];
}

const EnquirySettings: React.FC = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();

  const { data: configData, isLoading } = useGetCmsConfig<IEnquirySettingsData>("ENQUIRY_SETTINGS");

  const saveMutation = useSaveCmsConfig<IEnquirySettingsData>({
    page: "ENQUIRY_SETTINGS",
    onSuccess: () => messageApi.success("Enquiry notification settings saved."),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message ?? err?.message ?? "Failed to save settings"),
  });

  useEffect(() => {
    if (configData?.data) {
      form.setFieldsValue({
        to: configData.data.to || "",
        cc: (configData.data.cc || []).join(", "),
      });
    }
  }, [configData, form]);

  const onFinish = (values: { to: string; cc?: string }) => {
    const cc = (values.cc || "")
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    saveMutation.mutate({ to: values.to.trim(), cc });
  };

  return (
    <div className="max-w-2xl">
      <Card
        title={
          <div className="flex items-center gap-2">
            <Mail size={18} />
            <span>Enquiry Notification Settings</span>
          </div>
        }
      >
        {isLoading ? (
          <Spin />
        ) : (
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <p className="text-sm text-gray-500 mb-4">
              Every enquiry submitted from the sculpture or homepage enquiry form is emailed to the
              address below. Add more people to receive a copy using CC. Replies from this inbox go
              straight back to the customer who submitted the enquiry, not to ArtParkS.
            </p>

            <Form.Item
              label="Recipient email (To)"
              name="to"
              rules={[
                { required: true, message: "Please enter a recipient email" },
                { type: "email", message: "Please enter a valid email address" },
              ]}
            >
              <Input placeholder="peter@artparks.co.uk" />
            </Form.Item>

            <Form.Item
              label="CC (comma-separated, optional)"
              name="cc"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const emails = String(value).split(",").map((e: string) => e.trim()).filter(Boolean);
                    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const bad = emails.find((e: string) => !emailRe.test(e));
                    return bad ? Promise.reject(new Error(`"${bad}" is not a valid email`)) : Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="oliver.coquelin@gmail.com, someone.else@example.com" />
            </Form.Item>

            <Button type="primary" htmlType="submit" icon={<Save size={16} />} loading={saveMutation.isPending}>
              Save
            </Button>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default EnquirySettings;
